import React, { useState, useEffect, useMemo } from 'react';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import ScrollableTabs from '../../components/ScrollableTabs';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiCall from '../../shared/apiCall';
import { formatDateWithLocale } from '../../shared/utils';
import { isDayWithinAccommodationRange, findMultiDayAccommodations } from '../../shared/activityUtils';
import { buildTransportationSegments } from '../../shared/transportationSegments';
import LocationsTab from '../../components/LocationsTab';
import MainLocationsTab from '../../components/MainLocationsTab';
import WeatherTab from '../../components/WeatherTab';
import TransportationInfoTab from '../../components/TransportationInfoTab';
import DaysTabShare from '../components/DaysTabShare';
import InfoTabShare from '../components/InfoTabShare';
import ShareTabFeatureMessage from '../components/ShareTabFeatureMessage';
import PackingTab from '../../components/PackingTab';
import ContactTripModal from '../components/ContactTripModal';
import CopyItineraryModal from '../components/CopyItineraryModal';
import DayMapModal from '../../components/DayMapModal';
import VersionFooter from '../../components/VersionFooter';
import SEO from '../../components/SEO';
import LanguageSelector from '../../components/LanguageSelector';

export default function PlanDetailShare() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [plan, setPlan] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('days');
  const [locationTabKey, setLocationTabKey] = useState(Date.now());
  
  // State for minimized view
  const [isMinimized, setIsMinimized] = useState(false);
  
  // State for contact modal
  const [showContactModal, setShowContactModal] = useState(false);
  
  // State for copy itinerary modal
  const [showCopyItineraryModal, setShowCopyItineraryModal] = useState(false);
  
  // State for day map modal
  const [showDayMapModal, setShowDayMapModal] = useState(false);
  const [selectedDayForMap, setSelectedDayForMap] = useState(null);
  const [selectedDayIndexForMap, setSelectedDayIndexForMap] = useState(null);
  const [continuingAccommodationsForMap, setContinuingAccommodationsForMap] = useState([]);
  
  // State for sliding menu
  const [menuOpen, setMenuOpen] = useState(false);
  const [daysExpanded, setDaysExpanded] = useState(true);

  const transportationSegments = useMemo(() => {
    if (!parsedData) return { segments: [] };
    return buildTransportationSegments(parsedData);
  }, [parsedData]);

  // Load plan details when component mounts or planId changes
  useEffect(() => {
    if (!planId) {
      setError(t('planDetailShare.noPlanId'));
      setIsLoading(false);
      return;
    }
    
    fetchPlanDetails();
  }, [planId, t]);

  const fetchPlanDetails = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Call shared API to get plan details (no authentication required)
      const response = await apiCall(`/api/shared?action=GET_SHARED_PLAN_BY_QUERY&shareId=${planId}`, {}, 'GET');
      
      if (response.success) {
        const sharedPlan = response.plan;
        
        // Set plan data in the expected format
        setPlan({
          id: sharedPlan.id,
          name: sharedPlan.name,
          data: sharedPlan.data,
          allowContact: sharedPlan.allowContact || false,
          isTemplate: sharedPlan.isTemplate || false,
          createdAt: sharedPlan.createdAt,
          language: sharedPlan.language || 'en',
          ownerBanner: sharedPlan.ownerBanner || null
        });
        
        // Parse the plan data - it should already be cleaned
        setParsedData(sharedPlan.data);
      } else {
        setError(response.message || t('planDetailShare.failedToLoad'));
      }
    } catch (err) {
      console.error('Error fetching shared plan details:', err);
      setError(t('planDetailShare.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    
    // Force refresh of LocationsTab when switching to it
    if (key === 'locations') {
      setLocationTabKey(Date.now());
    }
  };

  // Handle contact form submission
  const handleContactSubmit = async (contactData) => {
    try {
      const response = await apiCall('/api/shared', {
        action: 'SUBMIT_CONTACT',
        shareId: planId,
        contactData
      }, 'POST');

      if (!response.success) {
        throw new Error(response.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact:', error);
      throw error;
    }
  };

  // Function to open day map modal
  const handleOpenDayMap = (day, dayIndex) => {
    setSelectedDayForMap(day);
    setSelectedDayIndexForMap(dayIndex);

    // Prepare continuing accommodations for this day (same logic as day view)
    const multiDayAccoms = findMultiDayAccommodations(parsedData);
    const continueAccoms = multiDayAccoms.filter(accom => 
      isDayWithinAccommodationRange(day.date, accom.startDateTime, accom.endDateTime) &&
      accom.originalDay !== day.date
    );
    setContinuingAccommodationsForMap(continueAccoms);

    setShowDayMapModal(true);
  };

  // Handle day selection from menu
  const handleDaySelect = (dayId) => {
    setMenuOpen(false);
    // Scroll to the day element
    const dayElement = document.getElementById(`day-${dayId}`);
    if (dayElement) {
      dayElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.mobile-sliding-menu') && 
          !event.target.closest('.mobile-menu-toggle')) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Generate SEO data based on plan
  const seoTitle = plan?.name ? `${plan.name} - Shared Trip Itinerary | trip147` : 'Shared Trip Itinerary | trip147';
  const seoDescription = plan?.name 
    ? `View the shared trip itinerary: ${plan.name}. Explore the travel plan with activities, accommodations, and more on trip147.`
    : 'View a shared trip itinerary on trip147. Explore travel plans with activities, accommodations, and detailed day-by-day schedules.';
  
  const structuredData = plan && parsedData ? {
    "@context": "https://schema.org",
    "@type": "TravelAction",
    "name": plan.name,
    "description": seoDescription,
    "object": {
      "@type": "TouristTrip",
      "name": plan.name,
      "itinerary": parsedData.days?.map((day, index) => ({
        "@type": "TouristDestination",
        "name": `Day ${index + 1}`,
        "description": day.activities?.map(a => a.name).join(', ') || ''
      })) || []
    }
  } : null;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        type="article"
        structuredData={structuredData}
      />
      {/* Override app-container padding for this page only */}
      <style>{`
        .app-container {
          padding-top: 0 !important;
        }
        .plan-detail-share-page {
          padding-top: 56px;
        }
        .mobile-sliding-menu .menu-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .days-list > div:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .plan-detail-share-page .scrollable-tabs-content {
          margin-top: 0.5rem !important;
        }
      `}</style>
      
      {/* Top Navigation Bar with Sliding Menu */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary mb-0" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1030 }}>
        <Container className="d-flex justify-content-between align-items-center">
          <Button 
            variant="outline-light"
            className="mobile-menu-toggle me-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-controls="mobile-menu"
            aria-expanded={menuOpen}
          >
            <span className="navbar-toggler-icon"></span>
          </Button>
          <div className="navbar-text text-white d-none d-md-block flex-grow-1">
            <span>{t('planDetailShare.tagline')}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <LanguageSelector variant="outline-light" size="sm" />
            <a href="/" className="navbar-brand fw-bold fs-4">
              <img src="/logo.svg" alt="trip147" style={{ width: '40px', height: '40px', marginRight: '10px' }} />
              trip147
            </a>
          </div>
        </Container>
      </nav>

      {/* Overlay when menu is open */}
      <div className={`menu-overlay ${menuOpen ? 'active' : ''}`} 
          onClick={() => setMenuOpen(false)}></div>

      {/* Sliding Menu */}
      <div className={`mobile-sliding-menu ${menuOpen ? 'open' : ''}`} id="mobile-menu" style={{ zIndex: 1040 }}>
        <div className="menu-content">
          {/* App name at the top */}
          <div 
            className="menu-header text-white fw-bold d-flex align-items-center" 
            style={{fontSize: '1.3rem', letterSpacing: '1px', cursor: 'pointer'}}
            onClick={() => {
              setMenuOpen(false);
              navigate('/');
            }}
          >
            <img src="/logo.svg" alt="trip147" style={{ width: '35px', height: '35px', marginRight: '10px' }} />
            trip147
          </div>
          
          {/* Main navigation items */}
          <div className="mobile-nav">
            <div 
              className="text-white menu-item d-flex align-items-center py-2 px-3"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setMenuOpen(false);
                navigate('/');
              }}
            >
              <i className="bi bi-house me-3"></i>{t('planDetailShare.home')}
            </div>
            <div 
              className="text-white menu-item d-flex align-items-center py-2 px-3"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setMenuOpen(false);
                setShowCopyItineraryModal(true);
              }}
            >
              <i className="bi bi-clipboard-plus me-3"></i>{t('planDetailShare.copyItinerary.title', { defaultValue: 'Copy Itinerary' })}
            </div>
            {plan?.allowContact && (
              <div 
                className="text-white menu-item d-flex align-items-center py-2 px-3"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setMenuOpen(false);
                  setShowContactModal(true);
                }}
              >
                <i className="bi bi-envelope me-3"></i>{t('planDetailShare.contactTripOwner')}
              </div>
            )}
          </div>

          {/* Days navigation section */}
          {parsedData && parsedData.days && parsedData.days.length > 0 && (
            <div className="days-section">
              <div 
                className="days-header d-flex justify-content-between align-items-center"
                onClick={() => setDaysExpanded(!daysExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <span><i className="bi bi-calendar me-2"></i> {t('planDetailShare.days')}</span>
                <i className={`bi ${daysExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
              </div>
              
              {daysExpanded && (
                <div className="days-list">
                  {parsedData.days.map((day, index) => (
                    <div 
                      key={day.id}
                      className="bg-transparent border-0 text-white py-2 px-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleDaySelect(day.id)}
                    >
                      <div className="d-flex align-items-center">
                        <i className="bi bi-calendar-date me-2"></i>
                        <span>{t('planDetailShare.dayLabel', { number: index + 1 })}{plan?.isTemplate ? '' : ` - ${day.date ? formatDateWithLocale(day.date, 'MMM d') : ''}`}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="plan-detail-share-page">
        <Container className="py-4">
        {/* Owner banner - fixed 80px height, 80% width, image contained (no clip/distort) */}
        {plan?.ownerBanner?.url && (
          <div className="mb-3 mx-auto" style={{ width: '80%', height: '80px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={plan.ownerBanner.url}
              alt={plan.ownerBanner.name || 'Banner'}
              style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
            />
          </div>
        )}
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}
        
        {/* Language mismatch message */}
        {plan?.language && plan.language !== i18n.language && (
          <Alert variant="info" className="mb-4">
            <i className="bi bi-info-circle me-2"></i>
            {t('planDetailShare.planLanguageMismatch', { 
              language: t(`planDetailShare.languageNames.${plan.language}`, { defaultValue: plan.language })
            })}
          </Alert>
        )}
        
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('planDetailShare.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('planDetailShare.loadingPlanDetails')}</p>
          </div>
        ) : plan && parsedData ? (
          <>
            {/* Header section */}
            <div className="mb-0">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                <div className="mb-0 mb-md-0">
                  <h1 className="h2 mb-0">{parsedData.name || 'Trip Plan'}</h1>
                </div>
                
                {/* Action Buttons - Copy Itinerary filled (stands out); Contact outline. Same size. Hidden on mobile. */}
                <div className="mt-2 mt-md-0 d-none d-md-block d-flex flex-wrap align-items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-3"
                    onClick={() => setShowCopyItineraryModal(true)}
                  >
                    <i className="bi bi-copy me-2"></i>
                    {t('planDetailShare.copyItinerary.title', { defaultValue: 'Copy Itinerary' })}
                  </Button>
                  {plan?.allowContact && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowContactModal(true)}
                    >
                      <i className="bi bi-envelope me-2"></i>
                      {t('planDetailShare.contactTripOwner')}
                    </Button>
                  )}
                </div>
              </div>
              
              {!plan?.isTemplate && (
              <p className="text-muted mb-0">
                {parsedData.startDate && parsedData.endDate && (
                  <>
                    <i className="bi bi-calendar me-1"></i>
                    <span className="d-none d-md-inline">{formatDateWithLocale(parsedData.startDate, 'PPP')} - {formatDateWithLocale(parsedData.endDate, 'PPP')}</span>
                    <span className="d-md-none">{formatDateWithLocale(parsedData.startDate, 'EEE, MMM d')} - {formatDateWithLocale(parsedData.endDate, 'EEE, MMM d')}</span>
                  </>
                )}
              </p>
            )}
            </div>
            
            <ScrollableTabs
              tabs={[
                { eventKey: 'days', title: t('planDetail.tabs.days') },
                { eventKey: 'locations', title: t('planDetail.tabs.locations') },
                { eventKey: 'main-locations', title: t('planDetail.tabs.mainLocations') },
                ...(plan?.isTemplate ? [] : [{ eventKey: 'weather', title: t('planDetail.tabs.weather') }]),
                { eventKey: 'costs', title: t('planDetail.tabs.costs') },
                { eventKey: 'transportation-info', title: t('planDetail.tabs.transportation') },
                { eventKey: 'info', title: t('planDetail.tabs.info') },
                { eventKey: 'packing', title: t('planDetail.tabs.packing') }
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            >
              <ScrollableTabs.Tab eventKey="days">
                <DaysTabShare 
                  parsedData={parsedData}
                  isMinimized={isMinimized}
                  onOpenDayMap={handleOpenDayMap}
                  shareId={planId}
                  planLanguage={plan?.language}
                  isTemplate={plan?.isTemplate}
                  onToggleMinimize={() => setIsMinimized(!isMinimized)}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="locations">
                <LocationsTab 
                  parsedData={parsedData}
                  planId={planId}
                  isActive={activeTab === 'locations'}
                  key={locationTabKey}
                  isTemplate={plan?.isTemplate}
                />
              </ScrollableTabs.Tab>
              
              <ScrollableTabs.Tab eventKey="main-locations">
                <MainLocationsTab 
                  parsedData={parsedData}
                  isTemplate={plan?.isTemplate}
                />
              </ScrollableTabs.Tab>
              
              {!plan?.isTemplate && (
              <ScrollableTabs.Tab eventKey="weather">
                <WeatherTab parsedData={parsedData} isActive={activeTab === 'weather'} />
              </ScrollableTabs.Tab>
              )}

              <ScrollableTabs.Tab eventKey="costs">
                <ShareTabFeatureMessage feature="costs" onCopyItineraryClick={() => setShowCopyItineraryModal(true)} />
              </ScrollableTabs.Tab>

              <ScrollableTabs.Tab eventKey="transportation-info">
                <TransportationInfoTab segments={transportationSegments.segments || []} isTemplate={plan?.isTemplate} />
              </ScrollableTabs.Tab>

              <ScrollableTabs.Tab eventKey="info">
                <InfoTabShare parsedData={parsedData} onCopyItineraryClick={() => setShowCopyItineraryModal(true)} />
              </ScrollableTabs.Tab>

              <ScrollableTabs.Tab eventKey="packing">
                {parsedData.packingItems && parsedData.packingItems.length > 0 ? (
                  <PackingTab parsedData={parsedData} onSaveChanges={() => {}} isActive={activeTab === 'packing'} readOnly />
                ) : (
                  <ShareTabFeatureMessage feature="packing" onCopyItineraryClick={() => setShowCopyItineraryModal(true)} />
                )}
              </ScrollableTabs.Tab>
            </ScrollableTabs>
          </>
        ) : (
          <Alert variant="warning">
            Failed to load plan data. Please try again.
          </Alert>
        )}
        </Container>
        
        {/* Contact Trip Modal */}
        <ContactTripModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          tripName={parsedData?.name || t('planDetailShare.tripPlan')}
          onSubmit={handleContactSubmit}
        />
        
        {/* Copy Itinerary Modal */}
        <CopyItineraryModal
          show={showCopyItineraryModal}
          onHide={() => setShowCopyItineraryModal(false)}
          shareId={planId}
        />
        
        {/* Day Map Modal */}
        <DayMapModal
          show={showDayMapModal}
          onHide={() => setShowDayMapModal(false)}
          day={selectedDayForMap}
          dayIndex={selectedDayIndexForMap}
          continuingAccommodations={continuingAccommodationsForMap}
        />
      </div>
      
      <VersionFooter />
    </>
  );
}
